(() => {
  const root = document.querySelector("[data-delivery-checker]");
  if (!root) return;

  const workerUrl    = root.dataset.workerUrl?.replace(/\/$/, "");
  const geoapifyKey  = root.dataset.geoapifyKey || "";

  if (!workerUrl) {
    console.error("[delivery-checker] worker_url is missing");
    return;
  }

  const elements = {
    form:         root.querySelector("[data-dc-form]"),
    title:        root.querySelector("[data-dc-title]"),
    input:        root.querySelector("[data-dc-input]"),
    button:       root.querySelector("[data-dc-btn]"),
    inputWrapper: root.querySelector("[data-dc-input-wrapper]"),
    subtitle:     root.querySelector("[data-dc-subtitle]"),
    result:       root.querySelector("[data-dc-result]"),
    resultDay:    root.querySelector("[data-dc-result-day]"),
    error:        root.querySelector("[data-dc-error]"),
    resultSlot:   root.querySelector("[data-dc-result-slot]"),
    slotHint:     root.querySelector("[data-dc-slot-hint]"),
    autocomplete: root.querySelector("[data-dc-autocomplete]"),
  };

  const required = ["form", "title", "input", "button", "inputWrapper", "subtitle", "result", "resultDay", "error"];
  for (const key of required) {
    if (!elements[key]) {
      console.error(`[delivery-checker] Missing element: ${key}`);
      return;
    }
  }

  const { form, title, input, button, inputWrapper, subtitle, result, resultDay, error,
          resultSlot, slotHint, autocomplete } = elements;

  const STATES = {
    INITIAL: "initial",
    LOADING: "loading",
    SUCCESS: "success",
    ERROR:   "error",
  };

  const STORAGE_KEY  = "delivery_checker_state";
  const ATTRACT_KEY  = "delivery_checker_attracted";
  const MIN_LOAD_MS  = 600;
  const MAX_LOAD_MS  = 900;

  let activeController      = null;
  let slotHintInterval      = null;
  let autocompleteController = null;
  let autocompleteTimer      = null;

  // ── helpers ──────────────────────────────────────────────────────────────

  function formatDeliveryDate(dateLabel, isoDate) {
    if (!isoDate) return dateLabel;
    const opts        = { timeZone: "Europe/London" };
    const todayISO    = new Date().toLocaleDateString("en-CA", opts);
    const tomorrow    = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowISO = tomorrow.toLocaleDateString("en-CA", opts);
    if (isoDate === todayISO)    return "Today";
    if (isoDate === tomorrowISO) return "Tomorrow";
    return dateLabel;
  }

  function setLoading(isLoading) {
    button.disabled = isLoading;
    button.classList.toggle("is-loading", isLoading);
  }

  function updateButtonStyle() {
    button.classList.toggle("is-soft", input.value.trim().length === 0);
  }

  // ── state ─────────────────────────────────────────────────────────────────

  const initialTitle = title.textContent;

  function showState(state, payload = {}) {
    const { deliveryDay = "", slotLabel = "", minutesUntilCutoff = null } = payload;

    subtitle.hidden = state !== STATES.INITIAL;
    error.hidden    = state !== STATES.ERROR;
    inputWrapper.classList.toggle("is-error", state === STATES.ERROR);

    if (state === STATES.INITIAL) title.textContent = initialTitle;
    if (state !== STATES.SUCCESS) result.hidden = true;

    if (state === STATES.SUCCESS) {
      resultDay.textContent = deliveryDay;
      if (resultSlot) resultSlot.textContent = (slotLabel && (deliveryDay === "Today" || deliveryDay === "Tomorrow")) ? ` (${slotLabel})` : "";
      title.textContent = "Shipping is available";

      result.style.opacity = "0";
      result.hidden = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { result.style.opacity = ""; });
      });
    }

    if (state === STATES.SUCCESS && minutesUntilCutoff !== null) {
      startSlotHint(minutesUntilCutoff);
    } else if (state !== STATES.SUCCESS) {
      stopSlotHint();
    }
  }

  // ── slot hint countdown ───────────────────────────────────────────────────

  function startSlotHint(initialMinutes) {
    stopSlotHint();
    if (!slotHint) return;

    let remaining = initialMinutes;

    const render = () => {
      if (remaining < 15) { slotHint.hidden = true; return; }
      slotHint.hidden = false;
      const display = Math.min(remaining, 60);
      slotHint.textContent = `Order within ${display} minutes to secure this delivery slot`;
    };

    render();
    if (remaining >= 15) {
      slotHintInterval = setInterval(() => {
        remaining--;
        render();
        if (remaining < 15) stopSlotHint();
      }, 60000);
    }
  }

  function stopSlotHint() {
    if (slotHintInterval) { clearInterval(slotHintInterval); slotHintInterval = null; }
    if (slotHint) slotHint.hidden = true;
  }

  // ── autocomplete ──────────────────────────────────────────────────────────

  function hideAutocomplete() {
    if (autocomplete) autocomplete.hidden = true;
  }

  function positionAutocomplete() {
    if (!autocomplete || !inputWrapper) return;
    const rect = inputWrapper.getBoundingClientRect();
    autocomplete.style.top   = (rect.bottom + window.scrollY + 4) + "px";
    autocomplete.style.left  = (rect.left + window.scrollX) + "px";
    autocomplete.style.width = rect.width + "px";
  }

  function renderAutocomplete(features) {
    if (!autocomplete) return;
    autocomplete.innerHTML = "";

    features.forEach(f => {
      const p          = f.properties;
      const postcode   = p.postcode     || "";
      const housenumber = p.housenumber || "";
      const street     = p.street       || "";
      const line1      = housenumber ? `${housenumber} ${street}`.trim() : (p.address_line1 || street);
      const city       = p.city         || p.county || "";

      const btn = document.createElement("button");
      btn.type  = "button";
      btn.className = "delivery-checker__autocomplete-item";

      // Always show postcode bold + readable address parts
      const addressParts = [line1, city].filter(Boolean).join(", ");
      btn.innerHTML = postcode
        ? `<strong>${postcode}</strong>${addressParts ? " — " + addressParts : ""}`
        : addressParts;

      btn.addEventListener("mousedown", e => {
        e.preventDefault();
        input.value = [line1, postcode].filter(Boolean).join(", ");
        updateButtonStyle();
        hideAutocomplete();

        try {
          const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            ...saved,
            selectedAddress: { postcode, street: line1, city },
          }));
        } catch {}
      });

      autocomplete.appendChild(btn);
    });

    if (features.length > 0) positionAutocomplete();
    autocomplete.hidden = features.length === 0;
  }

  async function fetchSuggestions(query) {
    if (!geoapifyKey || !autocomplete || query.length < 2) { hideAutocomplete(); return; }

    if (autocompleteController) autocompleteController.abort();
    autocompleteController = new AbortController();

    try {
      const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&apiKey=${geoapifyKey}&filter=countrycode:gb&limit=4`;
      const res  = await fetch(url, { signal: autocompleteController.signal });
      const data = await res.json();
      renderAutocomplete(data.features || []);
    } catch (err) {
      if (err.name !== "AbortError") hideAutocomplete();
    }
  }

  // ── button attract animation ──────────────────────────────────────────────

  function runAttractAnimation() {
    if (sessionStorage.getItem(ATTRACT_KEY)) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return; // user already interacted
    sessionStorage.setItem(ATTRACT_KEY, "1");

    setTimeout(() => {
      if (!button.classList.contains("is-soft")) return;
      button.classList.add("is-attract");
      button.addEventListener("animationend", () => {
        button.classList.remove("is-attract");
      }, { once: true });
    }, 700);
  }

  // ── events ────────────────────────────────────────────────────────────────

  input.addEventListener("input", () => {
    updateButtonStyle();

    if (input.value.trim() === "") {
      showState(STATES.INITIAL);
      sessionStorage.removeItem(STORAGE_KEY);
      hideAutocomplete();
      return;
    }

    if (inputWrapper.classList.contains("is-error")) showState(STATES.INITIAL);

    clearTimeout(autocompleteTimer);
    autocompleteTimer = setTimeout(() => fetchSuggestions(input.value.trim()), 300);
  });

  input.addEventListener("blur", () => {
    setTimeout(hideAutocomplete, 150);
  });

  document.addEventListener("click", e => {
    if (!root.contains(e.target)) hideAutocomplete();
  });


  form.addEventListener("submit", async event => {
    event.preventDefault();
    hideAutocomplete();

    const query = input.value.trim();
    if (!query) return;

    activeController?.abort();
    activeController = new AbortController();

    setLoading(true);
    showState(STATES.LOADING);

    const minDelay = MIN_LOAD_MS + Math.random() * (MAX_LOAD_MS - MIN_LOAD_MS);

    try {
      const [response] = await Promise.all([
        fetch(`${workerUrl}/check`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query }),
          signal: activeController.signal,
        }),
        new Promise(resolve => setTimeout(resolve, minDelay)),
      ]);

      const data = await response.json().catch(() => null);

      if (!response.ok || !data || typeof data !== "object" || !data.ok) {
        throw new Error("Invalid response");
      }

      const slotDateRaw        = data.nearestSlot?.date  || data.earliestDelivery || "";
      const slotIso            = data.nearestSlot?.iso   || "";
      const slotLabel          = data.nearestSlot?.label || "";
      const minutesUntilCutoff = data.minutesUntilNearestSlotCutoff ?? null;
      const deliveryDay        = formatDeliveryDate(slotDateRaw, slotIso);

      showState(STATES.SUCCESS, { deliveryDay, slotLabel, minutesUntilCutoff });

      const existing = (() => {
        try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
      })();

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...existing,
        state: STATES.SUCCESS,
        slotDateRaw,
        slotIso,
        slotLabel,
        minutesUntilCutoff,
        checkedAt: Date.now(),
        query: input.value.trim(),
      }));

    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("[delivery-checker]", err);
        showState(STATES.ERROR);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } finally {
      setLoading(false);
      updateButtonStyle();
    }
  });

  // ── init ──────────────────────────────────────────────────────────────────

  if (autocomplete) document.body.appendChild(autocomplete);

  updateButtonStyle();

  const saved = sessionStorage.getItem(STORAGE_KEY);
  if (saved) {
    try {
      const { state, slotDateRaw, slotIso, slotLabel, minutesUntilCutoff, checkedAt, query } = JSON.parse(saved);
      input.value = query || "";
      updateButtonStyle();

      let adjustedMinutes = minutesUntilCutoff;
      if (minutesUntilCutoff !== null && checkedAt) {
        adjustedMinutes = minutesUntilCutoff - Math.floor((Date.now() - checkedAt) / 60000);
      }

      const deliveryDay = formatDeliveryDate(slotDateRaw || "", slotIso || "");
      showState(state, { deliveryDay, slotLabel: slotLabel || "", minutesUntilCutoff: adjustedMinutes });
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
      showState(STATES.INITIAL);
    }
  } else {
    showState(STATES.INITIAL);
  }

  runAttractAnimation();
})();
